import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import * as authAPI from '../../api/auth.api';
import '../../styles/dossier.css';
import styles from './Security.module.css';

export default function Security() {
  const { user, fetchUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  const handleInitSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authAPI.setup2FA();
      setSetupData(data);
    } catch (err) {
      console.error('2FA_SETUP_FAILURE:', err);
      const backendErr = err?.response?.data?.error;
      const status = err?.response?.status;
      setError(backendErr ? `[${status}] ${backendErr}` : `Network Error or Backend Unreachable. Status: ${status || 'Unknown'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.verifyAndEnable2FA(setupData.secret, token);
      if (res.recoveryCodes) {
        setRecoveryCodes(res.recoveryCodes);
      }
      setMessage('Two-Factor Authentication enabled successfully!');
      setSetupData(null);
      setToken('');
      fetchUser(); // Refresh user state
    } catch (err) {
      setError(err?.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authAPI.disable2FA(disablePassword);
      setMessage('Two-Factor Authentication has been disabled.');
      setShowDisableConfirm(false);
      setDisablePassword('');
      fetchUser();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to disable 2FA.');
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
    alert('Recovery codes copied to clipboard!');
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <p className={`${styles.eyebrow} ink`}>{"// VAULT ACCESS CONTROL"}</p>
        <h1 className={`${styles.title} display`}>Security Settings</h1>
        <p className={`${styles.subtitle} bodyCopy`}>Manage your account protection and authentication methods.</p>
      </header>

      <div className={`${styles.section} paperShadow`}>
        <div className={styles.sectionHeader}>
          <h2 className={`${styles.sectionTitle} ink`}>Two-Factor Authentication (TOTP)</h2>
          <div className={`${styles.badge} ${user?.two_factor_enabled ? styles.active : styles.inactive}`}>
            {user?.two_factor_enabled ? 'ENABLED' : 'DISABLED'}
          </div>
        </div>

        <p className={`${styles.description} bodyCopy`}>
          Add an extra layer of security to your account by requiring a 6-digit code from an authenticator app (like Google Authenticator or Authy) whenever you log in.
        </p>

        {!user?.two_factor_enabled && !setupData && (
          <button className={styles.actionBtn} onClick={handleInitSetup} disabled={loading}>
            ENABLE 2FA
          </button>
        )}

        {setupData && (
          <div className={styles.setupBox}>
            <h3 className={`${styles.setupTitle} ink`}>Setup Instructions</h3>
            <ol className={`${styles.steps} bodyCopy`}>
              <li>Scan the QR code below with your Authenticator app.</li>
              <li>Alternatively, enter this secret key manually: <code className={styles.secret}>{setupData.secret}</code></li>
              <li>Enter the 6-digit code generated by the app to verify.</li>
            </ol>

            <div className={styles.qrContainer}>
              <img src={setupData.qrCodeDataURL} alt="2FA QR Code" className={styles.qrCode} />
            </div>

            <form onSubmit={handleVerifyAndEnable} className={styles.verifyForm}>
              <input
                type="text"
                placeholder="000000"
                maxLength="6"
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                className={styles.tokenInput}
                required
              />
              <div className={styles.formActions}>
                <button type="submit" className={styles.verifyBtn} disabled={loading || token.length !== 6}>
                  {loading ? 'VERIFYING...' : 'VERIFY & ENABLE'}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={() => setSetupData(null)}>
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        )}

        {user?.two_factor_enabled && !showDisableConfirm && (
          <div className={styles.enabledBox}>
            <p className={`${styles.successText} ink`}>&#10003; 2FA is currently protecting your account.</p>
            <button className={styles.disableBtn} onClick={() => setShowDisableConfirm(true)} disabled={loading}>
              DISABLE 2FA
            </button>
          </div>
        )}

        {user?.two_factor_enabled && showDisableConfirm && (
          <form onSubmit={handleDisable} className={styles.verifyForm}>
            <p className={`${styles.description} bodyCopy`}>
              This removes 2FA protection from your account. Confirm your current password to continue.
            </p>
            <input
              type="password"
              placeholder="Current password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className={styles.tokenInput}
              autoComplete="current-password"
              required
            />
            <div className={styles.formActions}>
              <button type="submit" className={styles.disableBtn} disabled={loading || !disablePassword}>
                {loading ? 'DISABLING...' : 'CONFIRM DISABLE'}
              </button>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => { setShowDisableConfirm(false); setDisablePassword(''); setError(''); }}
              >
                CANCEL
              </button>
            </div>
          </form>
        )}

        {recoveryCodes && (
          <div className={styles.recoveryBox}>
            <h3 className={`${styles.recoveryTitle} ink`}>Action Required: Save Recovery Codes</h3>
            <p className={`${styles.recoveryDesc} bodyCopy`}>
              If you lose access to your authenticator app, these codes can be used to log in.
              <strong> They will only be shown once.</strong> Store them in a secure place.
            </p>
            <div className={styles.codesGrid}>
              {recoveryCodes.map((code, i) => (
                <div key={i} className={styles.codeItem}>{code}</div>
              ))}
            </div>
            <button className={styles.copyBtn} onClick={copyCodes}>
              COPY TO CLIPBOARD
            </button>
          </div>
        )}

        {error && <div className={`${styles.errorMsg} ink`}>[ACCESS DENIED]: {error}</div>}
        {message && <div className={`${styles.successMsg} ink`}>{message}</div>}
      </div>
    </div>
  );
}
